import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';
import SocialMarquee from './SocialMarquee';
import { useAuth } from '../../context/AuthContext';

const rotatingWords = ["Ledgers", "Payments", "Invoices", "Cashflow"];

const HeroSection = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const { user } = useAuth();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative isolate overflow-hidden bg-[#fdfdfd] font-sans min-h-[90vh] flex flex-col justify-between">
      
      {/* Light Grid & Radial Background */}
      <div 
        className="absolute inset-0 -z-10 h-full w-full bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_20%,transparent_100%)] opacity-40"
      ></div>
      
      <div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 transform-gpu blur-[100px] pointer-events-none" aria-hidden="true">
        <div className="aspect-[1155/678] w-[40rem] bg-gradient-to-b from-[#ffe4e6] to-transparent opacity-60"></div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32 text-center relative z-10 flex-grow flex flex-col justify-center">
        
        {/* Top Red Subtitle */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-[#dc343b] text-xs font-bold tracking-[0.2em] uppercase mb-8 block">
            TRUSTED BY 1 MILLION+ MSMEs
          </span>
        </motion.div>

        {/* Main Heading with Rotating Word */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mx-auto max-w-4xl"
        >
          <h1 className="text-4xl font-extrabold tracking-tight text-[#0f172a] sm:text-6xl lg:text-[72px] leading-[1.1] mb-8" style={{ letterSpacing: '-0.02em' }}>
            Manage Your Daily <br />
            <span className="inline-block relative w-[240px] sm:w-[380px] text-center text-[#dc343b]">
              <AnimatePresence mode="wait">
                <motion.span
                  key={currentWordIndex}
                  initial={{ y: 20, opacity: 0, rotateX: -90 }}
                  animate={{ y: 0, opacity: 1, rotateX: 0 }}
                  exit={{ y: -20, opacity: 0, rotateX: 90 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  className="absolute left-0 right-0"
                  style={{ transformOrigin: "50% 50%" }}
                >
                  {rotatingWords[currentWordIndex]}
                </motion.span>
              </AnimatePresence>
              <span className="invisible">Invoices</span> {/* Invisible placeholder */}
            </span>
            <br />
            With Absolute Precision.
          </h1>
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mx-auto max-w-2xl"
        >
          <p className="text-lg leading-relaxed text-gray-500 font-medium">
            The most trusted digital ledger app for your business. 100% Free, Safe and Secure. Track your 'You Gave' and 'You Got' transactions anywhere, anytime.
          </p>
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <div className="flex -space-x-3">
            <img className="w-10 h-10 rounded-full ring-2 ring-white object-cover" src="https://i.pravatar.cc/150?img=31" alt="User" />
            <img className="w-10 h-10 rounded-full ring-2 ring-white object-cover" src="https://i.pravatar.cc/150?img=32" alt="User" />
            <img className="w-10 h-10 rounded-full ring-2 ring-white object-cover" src="https://i.pravatar.cc/150?img=33" alt="User" />
            <img className="w-10 h-10 rounded-full ring-2 ring-white object-cover" src="https://i.pravatar.cc/150?img=34" alt="User" />
          </div>
          <div className="text-left text-sm">
            <div className="font-bold text-gray-900">Enterprise-grade security</div>
            <div className="text-gray-500 text-xs">Automated daily backups</div>
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 flex justify-center"
        >
          {user ? (
            <Link
              to="/dashboard"
              className="group relative inline-flex items-center justify-center rounded-xl bg-[#dc343b] px-8 py-4 text-[15px] font-semibold text-white shadow-[0_8px_30px_rgba(220,52,59,0.3)] hover:shadow-[0_8px_30px_rgba(220,52,59,0.5)] hover:bg-[#b91c1c] transition-all duration-300 hover:-translate-y-0.5"
            >
              Go to Dashboard
              <ArrowRightIcon className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          ) : (
            <Link
              to="/register"
              className="group relative inline-flex items-center justify-center rounded-xl bg-[#dc343b] px-8 py-4 text-[15px] font-semibold text-white shadow-[0_8px_30px_rgba(220,52,59,0.3)] hover:shadow-[0_8px_30px_rgba(220,52,59,0.5)] hover:bg-[#b91c1c] transition-all duration-300 hover:-translate-y-0.5"
            >
              Start Using Free
              <ArrowRightIcon className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          )}
        </motion.div>

      </div>
      
      {/* Social Proof Band (Extracted Component) */}
      <SocialMarquee />
    </div>
  );
};

export default HeroSection;
